import { TaskStatus, Todo, TeamWithMembers } from '../../types';
import type { TodoFormRef } from '../../components/TodoForm';
import type { ViewType } from '../../components/ViewTabs';
import { useFilterState } from './useFilterState';
import { useFormDefaults } from './useFormDefaults';
import { useModalState } from './useModalState';

export interface AppState {
  // View state
  activeView: ViewType;
  setActiveView: (view: ViewType) => void;

  // Todo/Form state
  selectedTodo: Todo | null;
  setSelectedTodo: (todo: Todo | null) => void;
  showTodoForm: boolean;
  setShowTodoForm: (show: boolean) => void;
  todoFormRef: React.RefObject<TodoFormRef>;

  // Modal stack for modal return functionality
  modalStack: {
    previousModal?: string;
    previousPhase?: string;
    previousState?: Record<string, unknown>;
  };
  setModalStack: (stack: { previousModal?: string; previousPhase?: string; previousState?: Record<string, unknown> }) => void;

  // Todo creation state
  addingChildToParentId: string | null;
  setAddingChildToParentId: (id: string | null) => void;
  initialDueDate: string | null;
  setInitialDueDate: (date: string | null) => void;
  initialTodoDate: string | null;
  setInitialTodoDate: (date: string | null) => void;
  initialActiveInWeek: boolean;
  setInitialActiveInWeek: (active: boolean) => void;
  initialActiveInMonth: boolean;
  setInitialActiveInMonth: (active: boolean) => void;

  // Search/Filter state
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Pomodoro state
  showPomodoroTimer: boolean;
  setShowPomodoroTimer: (show: boolean) => void;
  currentPomodoroTask: Todo | null;
  setCurrentPomodoroTask: (task: Todo | null) => void;

  // AI Assistant state
  showAIAssistant: boolean;
  setShowAIAssistant: (show: boolean) => void;

  // Team state
  selectedTeamId: string | null;
  setSelectedTeamId: (id: string | null) => void;
  showTeamModal: boolean;
  setShowTeamModal: (show: boolean) => void;
  showJoinTeamModal: boolean;
  setShowJoinTeamModal: (show: boolean) => void;
  editingTeam: TeamWithMembers | null;
  setEditingTeam: (team: TeamWithMembers | null) => void;

  // List view filters
  listStatusFilter: TaskStatus[];
  setListStatusFilter: (filter: TaskStatus[]) => void;
  listHorizonFilter: string[];
  setListHorizonFilter: (filter: string[]) => void;
  listTagsFilter: string[];
  setListTagsFilter: (filter: string[]) => void;
  listTaskTypeFilter: 'all' | 'parent_only' | 'child_only';
  setListTaskTypeFilter: (filter: 'all' | 'parent_only' | 'child_only') => void;

  // Memoized filter state
  currentFilters: {
    statusFilter: TaskStatus[];
    horizonFilter: string[];
    tagsFilter: string[];
    taskTypeFilter: 'all' | 'parent_only' | 'child_only';
    setStatusFilter: (filter: TaskStatus[]) => void;
    setHorizonFilter: (filter: string[]) => void;
    setTagsFilter: (filter: string[]) => void;
    setTaskTypeFilter: (filter: 'all' | 'parent_only' | 'child_only') => void;
  };
}

export function useAppState(): AppState {
  const modalState = useModalState();
  const formDefaults = useFormDefaults();
  const filterState = useFilterState();

  return {
    // Modal/view state
    ...modalState,

    // Form default values
    ...formDefaults,

    // Filter state
    ...filterState
  };
}
